import ChevronRight from '@assets/icons/chevron-right.svg';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { GTINFilterCheckbox } from './gtin-filter-checkbox';
import { GTINFilters } from '@/hooks/use-gtin-metadata-filters';
import { List } from '@/components/list';
import { ListOption } from '@/components/list-item';
import { StyleSheet, Text, View } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useTheme } from '@/contexts/theme-context';
import { VariantPackInfoContext_GroupBadgeFieldsFragment } from '@/types';

export enum GTINFilterActionSheetRoute {
  BadgesFilter = 'BADGES_FILTER',
  Base = 'BASE',
  ListFilter = 'LIST_FILTER',
}

interface Props {
  badges: VariantPackInfoContext_GroupBadgeFieldsFragment[];
  badgesFilter: Set<string>;
  gtinFilters: GTINFilters;
  onBadgeFilterSelect: () => void;
  onListFilterSelect: (filterIndex: number) => void;
}

type FilterMetadata =
  | { type: 'badge' }
  | { listFilterIndex: number; type: 'list' }
  | {
      onChange: React.Dispatch<React.SetStateAction<boolean>>;
      toggled: boolean;
      type: 'flag';
    };
type GTINFilterListOption = ListOption<FilterMetadata>;

export const GTINFilterList: React.FC<Props> = ({
  badges,
  gtinFilters,
  onBadgeFilterSelect,
  onListFilterSelect,
  badgesFilter,
}) => {
  const theme = useTheme();
  const listData = useMemo<GTINFilterListOption[]>(() => {
    const options = gtinFilters.listFilters.reduce<GTINFilterListOption[]>(
      (acc, listFilter, idx) => {
        if (listFilter.isAvailable) {
          acc.push({
            metadata: {
              listFilterIndex: idx,
              type: 'list',
            },
            subtitle: listFilter.subtitle,
            title: listFilter.label,
            value: listFilter.label,
          });
        }
        return acc;
      },
      [],
    );
    if (Boolean(badges.length)) {
      options.push({
        metadata: {
          type: 'badge',
        },
        subtitle:
          badgesFilter.size === 0
            ? i18n.t('none')
            : badges
                .filter((b) => badgesFilter.has(b.uuid))
                .map((b) => b.name)
                .sort((a, b) => a.localeCompare(b))
                .join(', '),
        title: i18n.t('badges'),
        value: 'badges',
      });
    }
    for (const flagFilter of gtinFilters.flagFilters) {
      if (flagFilter.isAvailable) {
        options.push({
          metadata: {
            onChange: flagFilter.updateFilter,
            toggled: flagFilter.filterApplied,
            type: 'flag',
          },
          title: flagFilter.label,
          value: flagFilter.label,
        });
      }
    }

    return options;
  }, [badges, gtinFilters, badgesFilter]);

  return (
    <List
      data={listData}
      onSelect={({ metadata }) => {
        if (metadata?.type === 'badge') {
          onBadgeFilterSelect();
        } else if (metadata?.type === 'flag') {
          metadata.onChange((prev) => !prev);
        } else {
          onListFilterSelect(metadata!.listFilterIndex);
        }
      }}
      renderLeftComponent={({ metadata }) => {
        if (metadata?.type === 'badge' || metadata?.type === 'list') {
          return null;
        }
        return (
          <GTINFilterCheckbox
            isSelected={metadata!.toggled}
            onSelect={() => metadata!.onChange((prev) => !prev)}
          />
        );
      }}
      renderMainContent={(item) => (
        <View style={styles.mainContent}>
          <Text numberOfLines={2} style={[TYPOGRAPHY.listTitle, styles.title]}>
            {item.title}
          </Text>
          {item.subtitle ? (
            <Text numberOfLines={1} style={TYPOGRAPHY.listSubtitle}>
              {item.subtitle}
            </Text>
          ) : null}
        </View>
      )}
      renderRightComponent={({ metadata }) => {
        if (metadata?.type === 'flag') {
          return null;
        }
        return (
          <ChevronRight fill={theme.colors.primary} height={16} width={9} />
        );
      }}
      rightComponentStyle={styles.rightComponent}
    />
  );
};

const styles = StyleSheet.create({
  mainContent: {
    flex: 1,
  },
  rightComponent: {
    marginLeft: 16,
  },
  title: {
    lineHeight: 18,
  },
});
